import { Link } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { Facebook, Instagram, Twitter, Smartphone, Download } from 'lucide-react'
import { AsaasSeal, H3, Body, Caption } from '@meiuca/ui-components'
import { MeiucaLogo } from './MeiucaLogo'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents'
import { APP_STORE_URL, PLAY_STORE_URL } from '@/constants/appStoreLinks'
import { B2B_SITES } from '@/config/b2bSites'
import { env } from '@/lib/env'

interface FooterLinkItem {
  label: string
  href: string
  external?: boolean
  /** When set, clicks emit `nav_clicked` with this item id (source: footer). */
  trackId?: string
}

interface FooterSection {
  title: string
  links: FooterLinkItem[]
}

// Static, client-owned footer (replaces the old SDUI footer). Links point to
// real routes / flows — the marketing/system links the SDUI footer carried
// (arena-management, teacher-signup, quem-somos, …) were removed.
const SECTIONS: FooterSection[] = [
  {
    title: 'Explorar',
    links: [
      { label: 'Explorar tudo', href: '/explorar' },
      { label: 'Buscar arenas', href: '/arenas/search' },
      { label: 'Centros de treinamento', href: '/cts/search' },
      { label: 'Rotativos (Day Use)', href: '/rotativos/search' },
      { label: 'Indicar uma arena', href: '/suggest-arena' },
    ],
  },
  {
    title: 'Minha conta',
    links: [
      { label: 'Minhas reservas', href: '/reservations' },
      { label: 'Meu perfil', href: '/perfil' },
    ],
  },
  {
    // B2B links live here, not in the header — the header carries only the
    // end-client product. Customer-facing wording ("Sou dono de arena"),
    // never the internal system names.
    title: 'Para o seu negócio',
    links: [
      { label: 'Sou dono de arena', href: B2B_SITES.arenas, external: true, trackId: 'arenas' },
      {
        label: 'Sou professor / tenho CT',
        href: B2B_SITES.trainingCenters,
        external: true,
        trackId: 'teachers',
      },
    ],
  },
  {
    title: 'Suporte',
    links: [
      { label: 'Fale com a gente', href: env.SUPPORT_URL, external: true },
      { label: 'Excluir conta', href: '/excluir-conta' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Termos de uso', href: '/termos' },
      { label: 'Política de privacidade', href: '/privacidade' },
    ],
  },
]

const SOCIAL_LINKS = [
  { platform: 'instagram', url: 'https://instagram.com/meiuca', Icon: Instagram },
  { platform: 'facebook', url: 'https://facebook.com/meiuca', Icon: Facebook },
  { platform: 'twitter', url: 'https://twitter.com/meiuca', Icon: Twitter },
]

const APP_LINKS = [
  { platform: 'ios' as const, url: APP_STORE_URL },
  { platform: 'android' as const, url: PLAY_STORE_URL },
]

const linkClassName =
  'text-text-secondary hover:text-brand-primary transition-colors text-left text-sm'

function FooterLink({ link }: { link: FooterLinkItem }) {
  const handleClick = link.trackId
    ? () => analyticsService.track(ANALYTICS_EVENTS.NAV_CLICKED, { item: link.trackId, source: 'footer' })
    : undefined
  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className={linkClassName}
      >
        {link.label}
      </a>
    )
  }
  return (
    <Link to={link.href} className={linkClassName}>
      {link.label}
    </Link>
  )
}

export function MarketplaceFooter() {
  const year = new Date().getFullYear()

  return (
    <footer className="bg-background-white border-t border-border-light">
      <div className="max-w-7xl mx-auto px-4">
        {/* Main Footer Content */}
        <div className="py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {SECTIONS.map((section) => (
              <div key={section.title}>
                <H3 className="text-text-primary mb-4 text-base">{section.title}</H3>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <FooterLink link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border-light" />

        {/* Asaas seal — exigência regulatória do BaaS */}
        <div className="py-6 flex flex-col items-center gap-2 text-center">
          <AsaasSeal size="md" />
          <Caption className="text-text-tertiary max-w-md">
            Serviços financeiros prestados pelo ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A.,
            instituição autorizada pelo Banco Central do Brasil.
          </Caption>
        </div>

        {/* Divider */}
        <div className="border-t border-border-light" />

        {/* Bottom Section */}
        <div className="py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Logo and Copyright */}
            <div className="flex items-center gap-4">
              <MeiucaLogo symbolClassName="h-8 w-8 text-brand-primary" showWordmark={false} />
              <Caption className="text-text-tertiary">
                © {year} Meiuca. Todos os direitos reservados.
              </Caption>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {SOCIAL_LINKS.map(({ platform, url, Icon }) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-background-secondary hover:bg-brand-primary/10 flex items-center justify-center transition-colors"
                  aria-label={platform}
                >
                  <Icon className="w-5 h-5 text-text-secondary hover:text-brand-primary transition-colors" />
                </a>
              ))}
            </div>

            {/* App Download Links */}
            <div className="flex items-center gap-3">
              {APP_LINKS.map((app) => (
                <a
                  key={app.platform}
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-background-secondary hover:bg-border-light rounded-lg transition-colors"
                >
                  {app.platform === 'ios' ? (
                    <>
                      <Smartphone className="w-5 h-5 text-text-secondary" />
                      <div className="text-left">
                        <Caption className="text-text-tertiary text-xs">Download na</Caption>
                        <Body className="text-text-primary text-sm font-semibold">App Store</Body>
                      </div>
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 text-text-secondary" />
                      <div className="text-left">
                        <Caption className="text-text-tertiary text-xs">Baixar no</Caption>
                        <Body className="text-text-primary text-sm font-semibold">Google Play</Body>
                      </div>
                    </>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
